// ============================================================================
// Cinematic sound design — Web Audio synthesised (zero external assets)
// Ambient pad + interaction blips + voice events. Global mute respected.
// ============================================================================

let ctx = null;
let masterGain = null;
let padNodes = null;
let muted = true;

function ensureCtx() {
    if (ctx) return ctx;
    if (typeof window === "undefined") return null;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
    masterGain = ctx.createGain();
    masterGain.gain.value = 0;
    masterGain.connect(ctx.destination);
    return ctx;
}

export function setMuted(state) {
    muted = state;
    if (!ctx) return;
    const target = state ? 0 : 0.45;
    masterGain.gain.cancelScheduledValues(ctx.currentTime);
    masterGain.gain.linearRampToValueAtTime(target, ctx.currentTime + 0.6);
    if (state) {
        stopPad();
    } else {
        startPad();
    }
}

export function isMuted() { return muted; }

function startPad() {
    const c = ensureCtx();
    if (!c || padNodes) return;
    // Two slightly detuned low oscillators + noise through low-pass
    const osc1 = c.createOscillator();
    const osc2 = c.createOscillator();
    osc1.type = "sawtooth"; osc1.frequency.value = 55;  // A1
    osc2.type = "triangle"; osc2.frequency.value = 82.5; // E2
    const gain1 = c.createGain(); gain1.gain.value = 0.06;
    const gain2 = c.createGain(); gain2.gain.value = 0.05;

    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 320;
    lp.Q.value = 1.4;

    // breathy noise
    const bufferSize = 2 * c.sampleRate;
    const noiseBuffer = c.createBuffer(1, bufferSize, c.sampleRate);
    const out = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) out[i] = (Math.random() * 2 - 1) * 0.6;
    const noise = c.createBufferSource();
    noise.buffer = noiseBuffer; noise.loop = true;
    const noiseGain = c.createGain(); noiseGain.gain.value = 0.025;
    const noiseLp = c.createBiquadFilter();
    noiseLp.type = "lowpass"; noiseLp.frequency.value = 800;

    osc1.connect(gain1); osc2.connect(gain2);
    gain1.connect(lp); gain2.connect(lp);
    noise.connect(noiseLp); noiseLp.connect(noiseGain); noiseGain.connect(lp);
    lp.connect(masterGain);

    osc1.start(); osc2.start(); noise.start();

    // Slow LFO on lowpass cutoff
    const lfo = c.createOscillator();
    lfo.type = "sine"; lfo.frequency.value = 0.07;
    const lfoGain = c.createGain(); lfoGain.gain.value = 90;
    lfo.connect(lfoGain); lfoGain.connect(lp.frequency);
    lfo.start();

    padNodes = { osc1, osc2, noise, lfo };
}

function stopPad() {
    if (!padNodes) return;
    try { padNodes.osc1.stop(); padNodes.osc2.stop(); padNodes.noise.stop(); padNodes.lfo.stop(); } catch {}
    padNodes = null;
}

export function blip(kind = "click") {
    if (muted) return;
    const c = ensureCtx();
    if (!c) return;
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = "sine";
    const freq = kind === "hover" ? 1840 : kind === "success" ? 1760 : kind === "warn" ? 660 : 1200;
    o.frequency.setValueAtTime(freq, c.currentTime);
    o.frequency.exponentialRampToValueAtTime(freq * 0.6, c.currentTime + 0.12);
    g.gain.setValueAtTime(0, c.currentTime);
    g.gain.linearRampToValueAtTime(0.05, c.currentTime + 0.012); // softer envelope
    g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + 0.16);
    o.connect(g); g.connect(masterGain);
    o.start(); o.stop(c.currentTime + 0.18);
}

export function pulse() {
    if (muted) return;
    const c = ensureCtx();
    if (!c) return;
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = "sine";
    o.frequency.value = 110;
    g.gain.setValueAtTime(0, c.currentTime);
    g.gain.linearRampToValueAtTime(0.18, c.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + 0.9);
    o.connect(g); g.connect(masterGain);
    o.start(); o.stop(c.currentTime + 1.0);
}

const SPOKEN = new Set();
export function speak(text, key) {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
    if (muted) return;
    if (key && SPOKEN.has(key)) return;
    if (key) SPOKEN.add(key);
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    try {
        window.speechSynthesis.cancel();
        const u = new SpeechSynthesisUtterance(text);
        u.rate = 0.92; u.pitch = 0.75; u.volume = 0.45;
        window.speechSynthesis.speak(u);
    } catch {}
}

export function resumeAudio() {
    // Required after user gesture in many browsers
    if (ctx && ctx.state === "suspended") {
        ctx.resume();
    }
}
